import { useEffect, useRef, useState } from 'react';
import { Globe } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

export const UI_LANGS = ['es', 'de', 'fr', 'it', 'nl', 'ro', 'sv', 'no', 'da'];
export const PRODUCT_LANGS = UI_LANGS;
export const MAIN_PATHS = ['/', '/about', '/wines', '/beers', '/wine-tasting', '/spirits', '/contact', '/blog',
  '/faq', '/local-products', '/privacy-policy', '/terms', '/refund-policy'];
const isProductPath = (base) => base === '/products' || /^\/product\/[\w-]+$/.test(base);
const isSubPage = (base) => /^\/(blog|wines|beers)\/[\w-]+$/.test(base);
export const langsFor = (base) => (MAIN_PATHS.includes(base) || isSubPage(base) ? UI_LANGS : isProductPath(base) ? PRODUCT_LANGS : []);
const LANG_KEY = 'site_lang';
export const isTranslatable = (base) => langsFor(base).length > 0;
const ATTRS = ['alt', 'title', 'placeholder', 'aria-label'];
const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'svg']);

export const splitLang = (pathname) => {
  const m = pathname.match(/^\/(es|de|fr|it|nl|ro|sv|no|da)(\/.*?)?\/?$/);
  if (m && langsFor(m[2] || '/').includes(m[1])) return { lang: m[1], base: m[2] || '/' };
  return { lang: 'en', base: pathname.replace(/\/$/, '') || '/' };
};
export const langPath = (lang, base) => (lang === 'en' ? base : `/${lang}${base === '/' ? '' : base}`);

const skipNode = (el) => {
  for (let e = el; e && e !== document.body; e = e.parentElement) {
    if (SKIP.has(e.tagName) || e.getAttribute?.('translate') === 'no' || e.dataset?.noTranslate !== undefined) return true;
    const l = e.getAttribute?.('lang');
    if (l && !l.startsWith('en')) return true;
  }
  return false;
};

export const PageTranslator = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { lang, base } = splitLang(pathname);
  const active = lang !== 'en' && langsFor(base).includes(lang);
  const wasActive = useRef(false);

  useEffect(() => {
    if (!active) {
      const saved = sessionStorage.getItem(LANG_KEY);
      if (lang === 'en' && saved && saved !== 'en' && langsFor(base).includes(saved)) {
        navigate(langPath(saved, base) + window.location.search + window.location.hash, { replace: true });
        return undefined;
      }
      if (wasActive.current) window.location.reload();
      return undefined;
    }
    sessionStorage.setItem(LANG_KEY, lang);
    wasActive.current = true;
    document.documentElement.lang = lang;
    let dict = {};
    let done = new Set();
    let loaded = false;
    let cancelled = false;
    const loadTimers = [];
    const missing = new Set();
    const sent = new Set();
    let timer = null;
    let polls = 0;

    const mine = new WeakMap();
    const tr = (raw, node) => {
      const t = (raw || '').trim();
      if (!t || !/[A-Za-z]{2}/.test(t)) return null;
      if (node ? mine.get(node) === raw : done.has(t)) return null;
      if (dict[t]) return raw.replace(t, dict[t]);
      if (done.has(t)) return null;
      if (t.length < 2000) missing.add(t);
      return null;
    };
    const walk = () => {
      if (!loaded || cancelled) return;
      const w = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      for (let n = w.nextNode(); n; n = w.nextNode()) {
        if (skipNode(n.parentElement)) continue;
        const v = tr(n.nodeValue, n);
        if (v !== null && v !== n.nodeValue) { n.nodeValue = v; mine.set(n, v); }
      }
      document.body.querySelectorAll(ATTRS.map(a => `[${a}]`).join(',')).forEach(el => {
        ATTRS.forEach(a => { const cur = el.getAttribute(a); if (cur) { const v = tr(cur); if (v !== null && v !== cur) el.setAttribute(a, v); } });
      });
      document.body.querySelectorAll('a[href^="/"]').forEach(a => {
        const href = a.getAttribute('href');
        if (langsFor(href).includes(lang) && !skipNode(a)) a.setAttribute('href', langPath(lang, href));
      });
      const title = tr(document.title); if (title) document.title = title;
      ['meta[name="description"]', 'meta[property="og:title"]', 'meta[property="og:description"]'].forEach(sel => {
        const m = document.head.querySelector(sel); const v = m && tr(m.content); if (v) m.content = v;
      });
    };
    const schedule = () => { clearTimeout(timer); timer = setTimeout(walk, 60); };
    const flush = () => {
      if (!missing.size) return;
      const strings = [...missing].filter(s => !dict[s] && !done.has(s) && !sent.has(s)); missing.clear();
      strings.forEach(s => sent.add(s));
      if (!strings.length) return;
      fetch(`/api/i18n/${lang}/missing`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ strings }) }).catch(() => {});
      if (polls < 6) { polls += 1; loadTimers.push(setTimeout(load, 20000)); }
    };
    const load = () => fetch(`/api/i18n/${lang}`).then(r => r.json()).then(d => { if (cancelled) return; dict = d; done = new Set(Object.values(d)); loaded = true; walk(); }).catch(() => {});

    const obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: [...ATTRS, 'href'] });
    const headObs = new MutationObserver(schedule);
    headObs.observe(document.head, { childList: true, subtree: true, characterData: true });
    const onClick = (e) => {
      const a = e.target.closest?.('a[href]');
      const href = a?.getAttribute('href');
      if (href && href.startsWith(`/${lang}`) && !a.target && !e.metaKey && !e.ctrlKey) {
        e.preventDefault(); e.stopPropagation(); navigate(href);
      }
    };
    document.addEventListener('click', onClick, true);
    const flusher = setInterval(flush, 2500);
    load();
    return () => { cancelled = true; loadTimers.forEach(clearTimeout); obs.disconnect(); headObs.disconnect(); clearInterval(flusher); clearTimeout(timer); document.removeEventListener('click', onClick, true); };
  }, [active, lang, pathname, navigate]);
  return null;
};

const NAMES = { en: 'English', es: 'Español', de: 'Deutsch', fr: 'Français', it: 'Italiano', nl: 'Nederlands', ro: 'Română', sv: 'Svenska', no: 'Norsk', da: 'Dansk' };

export const MainLangSwitch = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { lang, base } = splitLang(pathname);
  const langs = langsFor(base);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (e.type === 'keydown' ? e.key === 'Escape' : !ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [open]);
  if (!langs.length) return null;
  return (
    <div ref={ref} className="fixed right-3 top-[112px] z-40 text-xs font-semibold" translate="no" data-no-translate data-testid="main-lang-switch">
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} aria-label="Choose language" className="flex items-center gap-1 bg-white/95 backdrop-blur rounded-full shadow px-3 py-1.5 text-gray-800 hover:bg-white" data-testid="main-lang-toggle">
        <Globe className="w-3.5 h-3.5" /> {lang.toUpperCase()}
      </button>
      {/* links stay in the DOM (hidden) so crawlers can follow every language version */}
      <nav className={`${open ? 'block' : 'hidden'} absolute right-0 mt-1 bg-white rounded-xl shadow-lg py-1 w-36`} aria-label="Languages">
        {['en', ...langs].map(l => (
          <a key={l} href={langPath(l, base)} hrefLang={l} onClick={() => sessionStorage.setItem(LANG_KEY, l)} className={`block px-3 py-1.5 ${l === lang ? 'text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-testid={`main-lang-${l}`}>{NAMES[l]}</a>
        ))}
      </nav>
    </div>
  );
};
